// Settings → Unity versions (api lib/unity-versions.ts): the catalog repo
// types pick their versions from (Apps Setup → Repos Setup). The base is the
// oldest version CI builds with and cannot be removed; a version still on a
// repo type is removed there first.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import {
  getUnityVersions, addUnityVersion, updateUnityVersion, deleteUnityVersion,
  type UnityVersion,
} from '@/api/app-builds';
import { UnityBadges } from '@/pages/apps-setup/UnityVersionPanel';
import { Button } from './Button';

export function UnityVersionsSettings() {
  const admin = useAuthStore((s) => s.admin);
  if (admin?.role !== 'super_admin' && !admin?.addons?.includes('developer')) return null;
  return <UnityVersionsCard />;
}

function UnityVersionsCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['unity-versions'], queryFn: getUnityVersions });
  const [version, setVersion] = useState('');
  const [lts, setLts] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['unity-versions'] });

  const add = useMutation({
    mutationFn: () => addUnityVersion({ version: version.trim(), is_lts: lts }),
    onSuccess: () => { setVersion(''); setLts(false); refresh(); },
  });
  const setLtsFlag = useMutation({
    mutationFn: (v: UnityVersion) => updateUnityVersion(v.version, { is_lts: !v.is_lts }),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (v: UnityVersion) => deleteUnityVersion(v.version),
    onSettled: refresh,
  });

  const rows = q.data ?? [];
  const error = [add.error, setLtsFlag.error, remove.error].find(Boolean);

  return (
    <div className="bg-surface border border-border rounded-lg mt-6">
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-lg font-medium text-content">Unity versions</h2>
        <p className="text-sm text-content-subtle mt-1">
          Every Unity version CI can build with. Each repo type picks its own from this list on Apps Setup → Repos Setup,
          where new apps draw theirs. The base is the oldest version CI accepts.
        </p>
      </div>
      <div className="px-6 py-5 space-y-4">
        {q.isLoading ? (
          <p className="text-sm text-content-subtle">Loading…</p>
        ) : q.error ? (
          <p className="text-sm text-danger">{(q.error as Error).message}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-content-subtle text-xs uppercase tracking-wide text-left">
                  <th className="py-1.5 pr-3 font-medium">Version</th>
                  <th className="py-1.5 pr-3 font-medium">Line</th>
                  <th className="py-1.5 pr-3 font-medium">LTS</th>
                  <th className="py-1.5 pr-3 font-medium">Apps</th>
                  <th className="py-1.5 pr-3 font-medium">Builds</th>
                  <th className="py-1.5 pr-3 font-medium">Repo types</th>
                  <th className="py-1.5 font-medium" />
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.version} className="border-t border-border/50 align-top">
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="text-content">{v.version}</code>
                        <UnityBadges isBase={!!v.is_base} isLts={false} />
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-content-muted">{v.line}</td>
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        checked={!!v.is_lts}
                        disabled={setLtsFlag.isPending}
                        onChange={() => setLtsFlag.mutate(v)}
                        aria-label={`LTS ${v.version}`}
                      />
                    </td>
                    <td className="py-2 pr-3 text-content-muted">{v.apps}</td>
                    <td className="py-2 pr-3 text-content-muted">{v.builds}</td>
                    <td className="py-2 pr-3 text-content-muted">{v.types.length ? v.types.join(', ') : '—'}</td>
                    <td className="py-2 text-right">
                      {!v.is_base && (
                        <button
                          type="button"
                          onClick={() => { if (window.confirm(`Remove Unity ${v.version} from the list?`)) remove.mutate(v); }}
                          className="text-red-500 hover:underline text-xs"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form
          className="flex flex-wrap items-end gap-3 pt-3 border-t border-border/50"
          onSubmit={(e) => { e.preventDefault(); if (version.trim()) add.mutate(); }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-xs text-content-subtle">Exact editor version</span>
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="6000.4.12f1"
              className="px-3 py-2 border border-border-strong rounded-md bg-surface text-content sm:text-sm font-mono"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-content-muted pb-2">
            <input type="checkbox" checked={lts} onChange={(e) => setLts(e.target.checked)} />
            LTS
          </label>
          <Button type="submit" variant="secondary" loading={add.isPending} disabled={!version.trim()}>
            Add
          </Button>
        </form>
        <p className="text-xs text-content-subtle">
          No “v” and no “LTS” in the version. CI refuses versions older than the base or without a Docker image.
        </p>
        {error && <p className="text-sm text-danger">{(error as Error).message}</p>}
      </div>
    </div>
  );
}
